/* ==========================================================================
   Evervain Studio — Dog Collar Font Picker
   --------------------------------------------------------------------------
   Product : personalized-floral-dog-collar-harness-leash-set (guarded below)

   What it does
     - hides the native <select name="properties[Font]"> (visually only)
     - renders an accessible listbox in its place, each row previewed in the
       real production font shipped in this theme's assets
     - the preview word follows the Pet Name field live, falling back to BELLA

   What it must never do
     - change the value submitted for properties[Font]. The hidden select keeps
       the original option values (e.g. "1-BebasNeue-Regular") and stays in the
       form, so the cart / order line item property is byte-identical to before.
     - touch the variant picker, the add to cart logic or any other field.
   ========================================================================== */
(() => {
  const HANDLE = 'personalized-floral-dog-collar-harness-leash-set';
  const currentHandle = () =>
    (location.pathname.match(/\/products\/([^/?#]+)/i)?.[1] || '').toLowerCase();
  if (currentHandle() !== HANDLE) return;

  /* The accordion snippet renders once for desktop and once for mobile, so this
     file can be requested twice. Only the first copy does any work. */
  if (window.evDogFontPickerLoaded) return;
  window.evDogFontPickerLoaded = true;

  const FALLBACK_SAMPLE = 'BELLA';
  const SAMPLE_MAX = 16;

  /* Index (the leading digit of the original option value) -> display label +
     the EV- family registered in assets/ev-font-preview.css.
     Matching on the leading index keeps this stable even if the metafield text
     is edited (e.g. "&" vs "and"), so no option can fall back to "unavailable". */
  const FONTS = {
    1: { label: 'Bebas Neue',     family: 'EV-BebasNeue' },
    2: { label: 'Caviar Dreams',  family: 'EV-CaviarDreams' },
    3: { label: 'Champagne',      family: 'EV-Champagne' },
    4: { label: 'Coolvetica',     family: 'EV-Coolvetica' },
    5: { label: 'Lobster',        family: 'EV-Lobster' },
    6: { label: 'Learning Curve', family: 'EV-LearningCurve' },
    7: { label: 'Southmore',      family: 'EV-Southmore' },
    8: { label: 'Blacksword',     family: 'EV-Blacksword' },
    9: { label: 'Dancing Script', family: 'EV-DancingScript' }
  };

  const BY_NAME = {
    bebasneueregular: 1,
    caviardreams: 2,
    champagnelimousines: 3,
    coolveticarg: 4,
    lobster: 5,
    learningcurve: 6,
    southmore: 7,
    blacksword: 8,
    dancingscript: 9
  };

  const metaFor = (value) => {
    const raw = String(value || '');
    const byIndex = Number(raw.match(/^\s*(\d)/)?.[1] || 0);
    if (FONTS[byIndex]) return FONTS[byIndex];
    const key = raw.toLowerCase().replace(/^\d+[\s\-_.]*/, '').replace(/[^a-z]/g, '');
    const idx = BY_NAME[key];
    if (FONTS[idx]) return FONTS[idx];
    return { label: raw.replace(/^\d+[\s\-_.]*/, '') || raw, family: '' };
  };

  const petNameField = (scope) =>
    scope.querySelector('input[name="properties[Pet Name]"]') ||
    scope.querySelector('input[name^="properties["][name*="Name" i]:not([name*="Font" i])');

  const sampleText = (scope) => {
    const raw = petNameField(scope)?.value?.trim() || '';
    if (!raw) return FALLBACK_SAMPLE;
    return raw.length > SAMPLE_MAX ? raw.slice(0, SAMPLE_MAX) + '…' : raw;
  };

  let uid = 0;

  const enhance = () => {
    const engine = document.querySelector('.ev-personalization-engine');
    if (!engine) return;

    const select = engine.querySelector('select[name="properties[Font]"]');
    if (!select || select.dataset.evDogFontEnhanced === 'true') return;
    if (!select.options.length) return;

    select.dataset.evDogFontEnhanced = 'true';
    select.classList.add('ev-dog-font-native');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');

    const seed = ++uid;
    const menuId = `EvDogFontMenu-${seed}`;
    const optId = (i) => `EvDogFontOption-${seed}-${i}`;

    const picker = document.createElement('div');
    picker.className = 'ev-dog-font-picker';

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'ev-dog-font-trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', menuId);

    const label = select.labels?.[0] || engine.querySelector(`label[for="${select.id}"]`);
    if (label) {
      if (!label.id) label.id = `EvDogFontLabel-${seed}`;
      trigger.setAttribute('aria-labelledby', `${label.id} ${menuId}-value`);
      label.addEventListener('click', (event) => {
        event.preventDefault();
        trigger.focus();
      });
    }

    const triggerName = document.createElement('span');
    triggerName.className = 'ev-dog-font-trigger__name';
    triggerName.id = `${menuId}-value`;

    const triggerSample = document.createElement('span');
    triggerSample.className = 'ev-dog-font-trigger__sample';
    triggerSample.setAttribute('aria-hidden', 'true');

    const chevron = document.createElement('span');
    chevron.className = 'ev-dog-font-trigger__chevron';
    chevron.setAttribute('aria-hidden', 'true');

    trigger.append(triggerName, triggerSample, chevron);

    const menu = document.createElement('ul');
    menu.className = 'ev-dog-font-menu';
    menu.id = menuId;
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('tabindex', '-1');
    menu.hidden = true;

    picker.append(trigger, menu);
    select.after(picker);

    /* ---------------------------------------------------------------- state */

    const options = [...select.options].map((option, index) => {
      const meta = metaFor(option.value);
      const row = document.createElement('li');
      row.className = 'ev-dog-font-option';
      row.id = optId(index);
      row.setAttribute('role', 'option');
      row.dataset.value = option.value;

      const name = document.createElement('span');
      name.className = 'ev-dog-font-option__name';
      name.textContent = `${index + 1} ${meta.label}`;

      const sample = document.createElement('span');
      sample.className = 'ev-dog-font-option__sample';
      sample.setAttribute('aria-hidden', 'true');
      if (meta.family) sample.style.fontFamily = `'${meta.family}', cursive`;

      row.append(name, sample);
      menu.append(row);
      return { el: row, sample, value: option.value, meta, index };
    });

    let activeIndex = Math.max(0, select.selectedIndex);

    const paintSamples = () => {
      const text = sampleText(engine);
      triggerSample.textContent = text;
      options.forEach((o) => { o.sample.textContent = text; });
    };

    const paintSelection = () => {
      const value = select.value;
      const current = options.find((o) => o.value === value) || options[0];
      triggerName.textContent = current ? current.meta.label : '';
      triggerSample.style.fontFamily = current?.meta.family
        ? `'${current.meta.family}', cursive`
        : '';
      options.forEach((o) => {
        o.el.setAttribute('aria-selected', String(o.value === value));
      });
    };

    const setActive = (index, scroll = true) => {
      activeIndex = Math.min(options.length - 1, Math.max(0, index));
      options.forEach((o, i) => o.el.classList.toggle('is-active', i === activeIndex));
      const active = options[activeIndex];
      if (!active) return;
      menu.setAttribute('aria-activedescendant', active.el.id);
      if (scroll) active.el.scrollIntoView({ block: 'nearest' });
    };

    const isOpen = () => !menu.hidden;

    const open = () => {
      if (isOpen()) return;
      paintSamples();
      menu.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      setActive(options.findIndex((o) => o.value === select.value));
      menu.focus({ preventScroll: true });
    };

    const close = (returnFocus) => {
      if (!isOpen()) return;
      menu.hidden = true;
      menu.removeAttribute('aria-activedescendant');
      trigger.setAttribute('aria-expanded', 'false');
      if (returnFocus) trigger.focus({ preventScroll: true });
    };

    /* The hidden select stays the single source of truth for the order value. */
    const commit = (index, returnFocus = true) => {
      const option = options[index];
      if (!option) return;
      if (select.value !== option.value) {
        select.value = option.value;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      paintSelection();
      close(returnFocus);
    };

    /* -------------------------------------------------------------- events */

    trigger.addEventListener('click', () => (isOpen() ? close(true) : open()));

    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        open();
      }
    });

    menu.addEventListener('keydown', (event) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setActive(activeIndex + 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          setActive(activeIndex - 1);
          break;
        case 'Home':
          event.preventDefault();
          setActive(0);
          break;
        case 'End':
          event.preventDefault();
          setActive(options.length - 1);
          break;
        case 'Enter':
        case ' ':
        case 'Spacebar':
          event.preventDefault();
          commit(activeIndex);
          break;
        case 'Escape':
          event.preventDefault();
          close(true);
          break;
        case 'Tab':
          close(false);
          break;
        default:
          break;
      }
    });

    menu.addEventListener('click', (event) => {
      const row = event.target.closest('.ev-dog-font-option');
      if (!row) return;
      commit(options.findIndex((o) => o.el === row));
    });

    menu.addEventListener('mousemove', (event) => {
      const row = event.target.closest('.ev-dog-font-option');
      if (row) setActive(options.findIndex((o) => o.el === row), false);
    });

    menu.addEventListener('focusout', (event) => {
      if (!picker.contains(event.relatedTarget)) close(false);
    });

    document.addEventListener('pointerdown', (event) => {
      if (!picker.contains(event.target)) close(false);
    });

    /* Live preview: typing in Pet Name repaints the samples only. No re-render,
       so Pet Name / Phone Number / Font / variant selection are all untouched. */
    engine.addEventListener('input', (event) => {
      const field = event.target;
      if (field instanceof HTMLInputElement && /pet\s*name/i.test(field.name || '')) {
        paintSamples();
      }
    });

    /* If something else changes the select (back button, another script), follow it. */
    select.addEventListener('change', paintSelection);

    paintSelection();
    paintSamples();
  };

  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    /* setTimeout, not requestAnimationFrame: rAF never fires while the tab is
       in the background, which would leave the picker unbuilt until focus. */
    setTimeout(() => {
      queued = false;
      enhance();
    }, 0);
  };

  const init = () => {
    enhance();
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', schedule);
})();
